import {defineStore} from 'pinia';
import {GameTypeId, LeagueId} from '@/model/core/core-data.model';
import {SiteConfiguration} from '@/model/site/context.model';
import {log} from '@/utils/logger';
import SEASON_SERVICE from '@/services/season.service';

const DEFAULT_LEAGUE_ID: LeagueId = 'a';
const DEFAULT_GAME_TYPE_ID: GameTypeId = 'm';

const STORAGE = 'storage';

type ContextState = {
	configuration: SiteConfiguration;
	focusSeasonId: string;
	focusLeagueId: LeagueId;
	focusGameTypeId: GameTypeId;
	focusTeamId: string | undefined;
	warnings: string[];
};

export const useContextStore = defineStore('context', {
	state: (): ContextState => ({
		configuration: {},
		focusSeasonId: SEASON_SERVICE.currentId(),
		focusLeagueId: DEFAULT_LEAGUE_ID,
		focusGameTypeId: DEFAULT_GAME_TYPE_ID,
		focusTeamId: undefined,
		warnings: [],
	}),
	getters: {
		siteConfig: (state) => state.configuration.site,
	},
	actions: {
		setConfiguration(configuration: SiteConfiguration) {
			this.configuration = configuration;
		},
		setFocusLeagueId(value: LeagueId | undefined) {
			this.focusLeagueId = value || DEFAULT_LEAGUE_ID;
		},
		setFocusSeasonId(value: string | undefined) {
			this.focusSeasonId = value || SEASON_SERVICE.currentId();
		},
		setFocusGameTypeId(value: GameTypeId | undefined) {
			this.focusGameTypeId = value || DEFAULT_GAME_TYPE_ID;
		},
		setFocusTeamId(value: string | undefined) {
			this.focusTeamId = value;
		},
		removeFocusTeamId() {
			this.focusTeamId = undefined;
		},
		addWarning(errorMsg: string) {
			this.warnings.push(errorMsg);
		},
	},
});

/** Mirrors the store into local storage after every change (kept from the Vuex setup). */
export function persistContext(store: ReturnType<typeof useContextStore>): void {
	store.$subscribe((mutation, state) => {
		log.debug('Mutation', mutation.type, mutation.storeId);
		localStorage.setItem(STORAGE, JSON.stringify({context: state}));
	});
}

export function clearStorage(): void {
	log.info(`Clear '${STORAGE}' from Local Storage`);
	localStorage.removeItem(STORAGE);
}

const PARENT_ORIGINS = ['https://www.hockeyfans.ch', 'https://hockeyfans.ch', 'https://preview.hockeyfans.ch'];

/**
 * The origin the height is posted to: an explicit ?origin= (local development), else the embedding hockeyfans.ch
 * host, read from the referrer, which is the parent page for an iframe.
 */
export function parentOrigin(search: string, referrer: string): string {
	const queried = new URLSearchParams(search).get('origin');
	if (queried) {
		return queried;
	}
	const referrerOrigin = referrer ? new URL(referrer).origin : undefined;
	return referrerOrigin && PARENT_ORIGINS.includes(referrerOrigin) ? referrerOrigin : PARENT_ORIGINS[0];
}

// read once: navigating inside the iframe drops the query and keeps the first referrer anyway
let targetOrigin: string | undefined;
let lastSentHeight: number | undefined;

// Send size to parent window (used for iframe resizing)
export function sendSize(tid?: string) {
	targetOrigin ??= parentOrigin(window.location.search, document.referrer);
	const el = document.body;
	const h = !!el && el.scrollHeight || 0;
	if (h !== lastSentHeight) {
		lastSentHeight = h;
		window.parent.postMessage(
			{
				type: tid || 'resize',
				height: h,
			},
			targetOrigin
		);
	}
}
